import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { Document } from "@/types/document";
import { useQuery, type Query } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

// BREEZEAI-1069: `refetchInterval` accepts either a static number of ms or a
// function that receives the current react-query Query and returns the next
// interval. The function form lets a caller poll fast while work is in
// progress and slow down (or stop) when the response says everything is
// idle, without any backend push infrastructure. `false` disables polling.
// Documents counted by their generation status for one ontology, over the same
// filters as the list (pagination ignored); legacy status columns are folded in
// server-side.
export interface OntologyStatusCounts {
  total: number;
  none: number;
  inprogress: number;
  // a V2 run's result waiting for approval: not written to the graph yet, so not in `done`
  review: number;
  done: number;
  error: number;
  cancelled: number;
}

// `statusCounts` on a list asked for with `withStatusCounts`: per ontology, plus
// how many documents are in progress for any ontology.
export interface DocumentStatusCounts {
  functional: OntologyStatusCounts;
  architecture: OntologyStatusCounts;
  any: { inprogress: number };
}

export interface DocumentListResponse {
  data: Document[];
  total: number;
  statusCounts?: DocumentStatusCounts;
}

export type DocumentServiceRefetchInterval =
  | number
  | false
  | ((
      query: Query<DocumentListResponse, Error>,
    ) => number | false | undefined);

export async function getDocumentByuuid(
  uuid: string,
  token: string,
  withFilter: boolean = false,
  page?: number,
  limit?: number,
  type: "document" | "artifact" | "code" = "document",
  // BREEZEAI-1069: filter and count-mode controls decoupled from `withFilter`
  // (which still controls field projection). `excludeCodeDerived` lets a caller
  // like the Data Sources detail page apply the same server-side "no internal
  // cluster docs" filter the dashboard uses without also switching to the
  // reduced-field response shape. `countOnly` asks the backend to skip the row
  // fetch entirely and return `{ data: [], total }` — used by count tiles so no
  // page-size can influence the number. `extraFilters` is an escape hatch for
  // ad-hoc Strapi-style filter query params the positional signature does not
  // model (e.g. `{ "filters[agent][$ne]": "architecture_agent" }` for the
  // ontology-manager's mode-scoped list); appended verbatim to the URL so the
  // aggregate `total` respects them.
  excludeCodeDerived?: boolean,
  countOnly?: boolean,
  extraFilters?: Record<string, string>,
  // also return `statusCounts` for the same filters
  withStatusCounts?: boolean,
): Promise<DocumentListResponse> {
  let url = `${config.isometricApiUrl}/documents?sortName=${extraFilters?.sortName ?? "updatedAt"}&sortOrder=${extraFilters?.sortOrder ?? "desc"}&filters[uuid][eq]=${uuid}&filters[type][eq]=${type}`;
  // `withFilter` is the dashboard's reduced-field summary/count mode. Internal
  // "code-derived" cluster documents are not user documents and must not be
  // counted, so exclude them server-side whenever we fetch in this mode. An
  // explicit `excludeCodeDerived` opts in without requiring the field-projection
  // reduction (needed by list callers that still want the full row shape).
  if (withFilter || excludeCodeDerived) {
    url += `&excludeCodeDerived=true`;
  }
  if (countOnly) {
    url += `&countOnly=true`;
  }
  if (page && limit) {
    url += `&page=${page}&limit=${limit}`;
  }
  if (withStatusCounts) {
    url += `&withStatusCounts=true`;
  }
  if (extraFilters) {
    for (const [k, v] of Object.entries(extraFilters)) {
      // sort keys override the default sort above instead of being appended twice
      if (k === "sortName" || k === "sortOrder") continue;
      url += `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
    }
  }
  // `ontologyStatus` (per-ontology generation status) is computed by the API
  // and returned on every row whatever the select.
  if (withFilter) {
    url +=
      "&selectFields=_id,agent,fileIndexedStatus,status,executionId";
  } else {
    // Project only the small metadata sub-keys the list actually reads
    // (fileName / fileType / artifactType for the row's type badge, git_url
    // so the "Open in new tab" action on document rows still works, tags for the
    // row's tag chips and the Manage Tags dialog). The
    // multi-MB `metadata.htmlContent` blob is deliberately excluded — it's
    // only needed when a row is opened, and the viewer pulls it via
    // `getArtifactContentOnDemand` at that point. Dot-notation sub-key
    // projection is honoured by the backend `/documents` list handler.
    url +=
      "&selectFields=_id,uuid,agent,type,metadata.fileName,metadata.fileType,metadata.artifactType,metadata.git_url,metadata.tags,createdAt,updatedAt,status,fileIndexedStatus,executionId,codeOntologyId,baseCommitId,headCommitId";
  }

  const response = await apiFetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error: any = new Error(`HTTP error! status: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const result = await response.json();
  return result;
}

export default function useGetDocumentService(
  uuid: string,
  enabled?: boolean,
  withFilter?: boolean,
  refetchInterval?: DocumentServiceRefetchInterval,
  page?: number,
  limit?: number,
  type: "document" | "artifact" | "code" = "document",
  excludeCodeDerived?: boolean,
  countOnly?: boolean,
  extraFilters?: Record<string, string>,
  withStatusCounts?: boolean,
  // e.g. { refetchOnMount: "always" } for a tile that must be fresh on every visit
  queryOptions?: { refetchOnMount?: boolean | "always"; staleTime?: number },
) {
  const { getAuthHeaders } = useAuthHeaders();
  // Stable, order-independent cache key for the ad-hoc filters: react-query
  // treats object identity as key changes, so passing a fresh literal each
  // render would cause a refetch loop.
  const extraFiltersKey = extraFilters
    ? Object.keys(extraFilters).sort().map((k) => `${k}=${extraFilters[k]}`).join("&")
    : "";

  return useQuery({
    queryKey: [
      withFilter ? "document-by-id-with-filter" : "document-by-id",
      uuid,
      withFilter,
      page,
      limit,
      type,
      excludeCodeDerived ?? false,
      countOnly ?? false,
      extraFiltersKey,
      withStatusCounts ?? false,
    ],
    queryFn: () => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return getDocumentByuuid(
        uuid || "",
        token,
        withFilter,
        page,
        limit,
        type,
        excludeCodeDerived,
        countOnly,
        extraFilters,
        withStatusCounts,
      );
    },
    enabled: !!enabled,
    refetchInterval: refetchInterval ?? false,
    placeholderData: (prev) => prev,
    ...(queryOptions?.staleTime !== undefined ? { staleTime: queryOptions.staleTime } : {}),
    ...(queryOptions?.refetchOnMount !== undefined
      ? { refetchOnMount: queryOptions.refetchOnMount }
      : {}),
  });
}

export async function getArtifactContentOnDemand(
  id: string,
  token: string
): Promise<{ data: Document[] }> {
  // View-time fetch: `content` (Markdown FRD) plus `metadata.htmlContent`
  // (the standalone rich HTML render). Both travel on a user-initiated View
  // click — never on the list load — so the pre-load CPU spike caused by
  // pulling htmlContent for every row of the artifacts list is gone, but the
  // panel can still render the rich HTML for HTML-mode artifacts.
  const url = `${config.isometricApiUrl}/documents?filters[_id][eq]=${id}&selectFields=content,metadata.htmlContent`;

  const response = await apiFetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error: any = new Error(`Failed to fetch artifact content: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function getDocumentsSignedUrlById(
  id: string | number,
  headers: Record<string, string>
) {
  const url = `${config.isometricApiUrl}/documents/get-signed-url-by-id/${id}?sortName=updatedAt&sortOrder=desc`;
  const response = await apiFetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  // Check if the response is ok (status code in the range 200-299)
  if (!response.ok) {
    const error: any = new Error(`HTTP error! status: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return await response.text();
}

